/* eslint-disable max-statements */
/* eslint-disable max-lines */
import type React from 'react';
import { useCallback, useContext, useMemo, useState } from 'react';
import { CONTROLLED_DATE_TIME_PICKER_TYPES } from '../../../constants/ControlledDateTimePickerTypes.js';
import { ControlledDateTimePickerContext } from '../../../ControlledDateTimePickerCTX.js';
import type { DSControlledDateTimePickerInternalsT } from '../../../sharedTypes.js';
import { CalendarContext } from '../Calendar/CalendarContext.js';
import { TimeWheelContext } from '../TimeWheel/TimeWheelContext.js';
export interface CalendarWithTimeWheelContextT {
  referenceElement: HTMLButtonElement | null;
  ariaCurrentValueForInputs: string;
  showCalendarWithTimeWheel: boolean;
  closeCalendar: () => void;
  handleToggleCalendarWithTimeWheel: (
    e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
  ) => void;
  handleSetTriggerRef: (instance: HTMLButtonElement) => void;
  handleMenuWrapperKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
  handlePickerIconKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
  isControllerOnly: boolean;
  disabled: boolean;
  applyAriaDisabled?: boolean;
}
export const useConfigCalendarWithTimeWheelCTX = (): CalendarWithTimeWheelContextT => {
  const {
    pickerButtonRef,
    props: { type, onPickerOpen, onPickerClose, disabled, applyAriaDisabled },
    withAnyInputs,
    isWithTimeInputs,
    ariaCurrentValueForInputs,
    trackFocusFirstSegment,
    trackFocusMeridiemInput,
    trackFocusYearInput,
    trackFocusPicker,
    latestInteractionRegion,
    currFocusDescriber,
  } = useContext(ControlledDateTimePickerContext);
  const { handleToggleCalendar } = useContext(CalendarContext);
  const { handleToggleTimePicker } = useContext(TimeWheelContext);
  const [showCalendarWithTimeWheel, setShowCalendarWithTimeWheel] = useState(false);
  const onHomeKeyDown = useCallback(() => {
    trackFocusFirstSegment();
  }, [trackFocusFirstSegment]);
  const handlePickerIconKeyDown: DSControlledDateTimePickerInternalsT.OnKeyDown<HTMLButtonElement> = useCallback(
    (e) => {
      const { key } = e;
      if (['Home', 'End'].includes(key)) e.preventDefault();
      if (['Home', 'End'].includes(key)) e.stopPropagation();
      if (key === 'Home') onHomeKeyDown();
      if (key === 'Backspace' && withAnyInputs) {
        if (isWithTimeInputs) trackFocusMeridiemInput();
        else trackFocusYearInput();
      }
    },
    [isWithTimeInputs, onHomeKeyDown, trackFocusMeridiemInput, trackFocusYearInput, withAnyInputs],
  );
  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement | null>(null);
  const openCalendar = useCallback(() => {
    if (disabled) return;
    setShowCalendarWithTimeWheel(true);
    onPickerOpen();
    setTimeout(() => handleToggleCalendar());
    setTimeout(() => handleToggleTimePicker());
  }, [handleToggleCalendar, handleToggleTimePicker, onPickerOpen, disabled]);
  const closeCalendar = useCallback(() => {
    setShowCalendarWithTimeWheel(false);
    onPickerClose();
    setTimeout(() => handleToggleCalendar());
    setTimeout(() => handleToggleTimePicker());
  }, [handleToggleCalendar, handleToggleTimePicker, onPickerClose]);

  const handleToggleCalendarWithTimeWheel = useCallback(() => {
    if (!showCalendarWithTimeWheel) openCalendar();
    else closeCalendar();
  }, [closeCalendar, openCalendar, showCalendarWithTimeWheel]);

  const handleSetTriggerRef = useCallback(
    (newRef: HTMLButtonElement) => {
      pickerButtonRef.current = newRef;
      setReferenceElement(newRef);
      if (latestInteractionRegion === 'picker-icon' && currFocusDescriber === 'datetime-picker-icon') newRef?.focus();
    },
    [currFocusDescriber, latestInteractionRegion, pickerButtonRef],
  );
  const handleMenuWrapperKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      const { key } = e;
      if (key === 'Escape') {
        closeCalendar();
        trackFocusPicker();
      }
    },
    [closeCalendar, trackFocusPicker],
  );

  const isControllerOnly = useMemo(() => type === CONTROLLED_DATE_TIME_PICKER_TYPES.DATE_TIME.CONTROLLER_ONLY, [type]);

  return useMemo(
    () => ({
      referenceElement,
      showCalendarWithTimeWheel,
      closeCalendar,
      handleToggleCalendarWithTimeWheel,
      handleSetTriggerRef,
      handleMenuWrapperKeyDown,
      handlePickerIconKeyDown,
      isControllerOnly,
      disabled,
      applyAriaDisabled,
      ariaCurrentValueForInputs,
    }),
    [
      referenceElement,
      showCalendarWithTimeWheel,
      closeCalendar,
      handleToggleCalendarWithTimeWheel,
      handleSetTriggerRef,
      handleMenuWrapperKeyDown,
      handlePickerIconKeyDown,
      isControllerOnly,
      disabled,
      applyAriaDisabled,
      ariaCurrentValueForInputs,
    ],
  );
};
